import configTokens from 'config/constants/tokens'
import { Network, Token } from 'config/types'
import { QueryDepositInfo } from 'config/types/transaction'
import useModal from 'hooks/useModal'
import Column, { ColumnBetween } from 'layout/Components/Column'
import { RowCenter } from 'layout/Components/Row'
import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Button from 'UIKit/Button'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import TransText from 'UIKit/Text/TransText'
import TokenLogo from 'UIKit/TokenLogo'
import ChainLogo from 'UIKit/TokenLogo/ChainLogo'
import { getNetworkInfo } from 'utils/network'
import { useTokensWithSameCode } from 'views/Payment/MobilePayment/hooks/useNetwork'
import { PaymentLabel, PaymentStroke, TextSelect } from '../../../styles'
import { useFindDeposit } from '../hooks'
import FilterInput from './FilterInput'
import HashInput from './HashInput'
import NetworkSelectModal from './NetworkSelectModal'
import ResultInfo from './ResultInfo'
import TokenSelectModal from './TokenSelectModal'

const SelectItemLayout = ({ children, title }: { children: React.ReactNode; title: string }) => {
  return (
    <Column gap="var(--pm-space-label, 8px)">
      <PaymentLabel>
        <Trans>{title}</Trans>
      </PaymentLabel>
      {children}
    </Column>
  )
}

const ResultSection = ({
  submiting,
  isError,
  depositInfo,
}: {
  submiting: boolean
  isError: boolean
  depositInfo: QueryDepositInfo
}) => {
  return (
    <>
      {submiting ? (
        <RowCenter width="100%" height="60px">
          <CircleLoader />
        </RowCenter>
      ) : isError ? (
        <TransText fontSize="12px" color="text_secondary" letterSpacing="0.2px">
          Sorry! This is an invalid TxID/TxHash. Please double check on blockchain explorer. If your deposit is
          confirmed in the blockchain, you can contact our Support with your deposit info so we can help.
        </TransText>
      ) : depositInfo?.depositInfo?.currency && depositInfo?.depositInfo?.network ? (
        <ResultInfo
          token={configTokens[depositInfo?.depositInfo?.network]?.[depositInfo?.depositInfo?.currency]}
          value={depositInfo?.depositInfo?.value}
          status={depositInfo?.depositInfo?.status}
          createTime={depositInfo?.depositInfo?.createTime}
        />
      ) : null}
    </>
  )
}

const HistoryInfo = () => {
  const { t } = useTranslation()
  const [selectedToken, setSelectedToken] = useState<Token | null>(null)
  const [selectedNetwork, setSelectedNetwork] = useState<Network | null>(null)
  const [hash, setHash] = useState<string>('')
  const payloadRef = useRef({
    selectedToken: null,
    selectedNetwork: null,
    transactionHash: '',
  })

  const { depositInfo, submiting, handleSubscribeDeposit, isError } = useFindDeposit()

  const [onPresentToken] = useModal(TokenSelectModal)
  const [onPresentNetwork] = useModal(NetworkSelectModal)

  const sameCodeTokens = useTokensWithSameCode(selectedToken)
  const isSingleNetwork = !(sameCodeTokens.length > 1)

  const onNetworkSelect = (network: Network) => {
    setSelectedNetwork(network)
  }

  useEffect(() => {
    if (selectedToken && isSingleNetwork) {
      onNetworkSelect(getNetworkInfo(selectedToken.network)?.network)
    }
  }, [selectedToken?.code])

  const handleCurrencySelect = (token: Token) => {
    setSelectedToken(token)
    setSelectedNetwork(null)
  }

  const openTokenSelectModal = () => {
    onPresentToken({
      onCurrencySelect: handleCurrencySelect,
      currencySelected: selectedToken,
    })
  }

  const openNetworkSelectModal = () => {
    onPresentNetwork({
      currencySelected: selectedToken,
      networkSelected: selectedNetwork,
      onCurrencySelect: (token) => {
        setSelectedToken(token)
      },
      onNetworkSelect,
    })
  }

  const onHashChange = (hash: string) => {
    setHash(hash)
  }

  const isSamePayload =
    selectedToken?.code === payloadRef.current.selectedToken?.code &&
    selectedNetwork?.chainId === payloadRef.current.selectedNetwork?.chainId &&
    hash === payloadRef.current.transactionHash

  const isDisabledSubmit = !selectedToken || !selectedNetwork || !hash || submiting || isSamePayload

  const onFindDeposit = () => {
    if (isSamePayload) {
      return
    }

    payloadRef.current = {
      selectedToken,
      selectedNetwork,
      transactionHash: hash,
    }

    handleSubscribeDeposit({
      selectedToken,
      selectedNetwork,
      transactionHash: hash,
    })
  }

  return (
    <ColumnBetween height="fit-content" minHeight="100%" gap="var(--pm-space-component, 20px)">
      <Column gap="var(--pm-space-component, 20px)">
        <SelectItemLayout title={t('Select Coin')}>
          <FilterInput
            onClick={openTokenSelectModal}
            selectedName={
              selectedToken ? (
                <TextSelect color="currentColor">{selectedToken?.name}</TextSelect>
              ) : (
                <TextSelect color="text_secondary_hover">
                  <Trans>Select Coin</Trans>
                </TextSelect>
              )
            }
            logo={
              selectedToken ? <TokenLogo token={selectedToken} hideNetwork size={20} width={20} height={20} /> : null
            }
          />
        </SelectItemLayout>

        <SelectItemLayout title={t('Network')}>
          <FilterInput
            onClick={selectedToken ? () => openNetworkSelectModal() : null}
            selectedName={
              (isSingleNetwork && selectedNetwork) || selectedNetwork ? (
                <TextSelect color="currentColor">{selectedNetwork?.networkInfo?.displayName}</TextSelect>
              ) : (
                <TextSelect color={selectedToken ? 'text_secondary' : 'text_disabled'}>
                  <Trans>Choose Network</Trans>
                </TextSelect>
              )
            }
            logo={selectedNetwork ? <ChainLogo network={selectedNetwork?.chainId} width={20} height={20} /> : null}
          />
        </SelectItemLayout>

        <SelectItemLayout title="TxID/TxHash">
          <HashInput hash={hash} onChange={onHashChange} />
        </SelectItemLayout>

        <PaymentStroke />

        <ResultSection submiting={submiting} isError={isError} depositInfo={depositInfo} />
      </Column>

      <Button onClick={onFindDeposit} disabled={isDisabledSubmit} minHeight="46px">
        <TransText fontSize="14px" fontWeight="600">
          Find Deposit
        </TransText>
      </Button>
    </ColumnBetween>
  )
}

export default HistoryInfo
